import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { readImpersonation, stopImpersonation } from "../../auth/impersonation";
import { setCredentials } from "../../store/actions/authActions";
import { routePathsByRole } from "../../constants";
import { Badge, Btn } from "../ForBrands/ui/BrandUI";
import { bannerPlan, usePlan } from "../common/PlanBadge";
import "../ForBrands/brandMode.css";

const roleLabel = (permissions) => {
  for (const key of Object.keys(routePathsByRole)) {
    if ((permissions || []).includes(key)) return routePathsByRole[key].role;
  }
  return "User";
};

// Sticky bar shown in a tab that an admin opened as another user. Also
// seeds the redux auth token for API helpers that read it from the store.
// It sits above every route of that tab (V2 shells and legacy pages), so it
// stays one compact row on the bm tokens and keeps its sticky top-0 z-[200]
// place above the shells' own topbars. The account's plan follows it, as
// the user sees it (" · Premium (team plan)" for an athlete their team's
// coach covers), once the entitlement has loaded.
const ImpersonationBanner = () => {
  const session = useMemo(() => readImpersonation(), []);
  const dispatch = useDispatch();
  const authToken = useSelector((s) => s.auth?.token);
  const authUser = useSelector((s) => s.auth?.user);
  const profile = useSelector((s) => s.user?.user);
  const plan = usePlan();
  // Seed the auth store the way login does: the token right away, and the
  // user record once the profile fetch lands (paywall and subscription
  // status read it from there).
  useEffect(() => {
    if (!session) return;
    const loaded = profile && profile.email && profile.email.toLowerCase() === session.email.toLowerCase() ? profile : null;
    if (authToken !== session.token || (loaded && authUser?.email !== loaded.email)) {
      dispatch(setCredentials({ token: session.token, user: loaded || authUser || null }));
    }
  }, [session, authToken, authUser, profile, dispatch]);
  if (!session) return null;
  return (
    <div
      className="bm-root sticky top-0 z-[200] flex flex-wrap items-center justify-between gap-2 border-b border-bm-border bg-bm-subtle px-4 py-2"
      role="status"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <Badge tone="brand">Admin view</Badge>
        <span className="truncate bm-body-s text-bm-ink">
          Signed in as <span className="font-semibold">{session.name}</span> ({roleLabel(session.permissions)}, {session.email}){bannerPlan(plan)}.
          Everything you do here happens as this user.
        </span>
      </div>
      <Btn variant="secondary" size="sm" onClick={stopImpersonation}>
        Exit user view
      </Btn>
    </div>
  );
};

export default ImpersonationBanner;
